import { Button, Card, RefreshIcon } from '@/lib';
import React, { FC } from 'react';
import { MarketIntelligenceReportData } from '../../types';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

const REPORT_META_ITEMS = [
  { id: 'analyzedDomain', translationKey: 'report.meta.analyzedDomain' },
  { id: 'industry', translationKey: 'report.meta.industry' },
  { id: 'scopeLabel', translationKey: 'report.meta.scope' },
  { id: 'generatedAt', translationKey: 'report.meta.generatedAt' },
] as const satisfies ReadonlyArray<{
  id: keyof Pick<MarketIntelligenceReportData, 'analyzedDomain' | 'industry' | 'scopeLabel' | 'generatedAt'>;
  translationKey: string;
}>;

const ReportMetaItem: FC<{ value: string; label: string }> = ({ value, label }) => (
  <div className='min-w-0'>
    <p className='text-primary-900 truncate text-sm font-semibold'>{value}</p>
    <p className='text-xs text-primary-600'>{label}</p>
  </div>
);

export const ReportMetaItems = ({
  report,
  onRegenerate,
  isGenerating,
}: {
  report: MarketIntelligenceReportData;
  onRegenerate: () => void;
  isGenerating: boolean;
}) => {
  const { t } = useTranslation('marketIntelligence');

  return (
    <Card className='block border-primary-border bg-white p-6 drop-shadow-none'>
      <div className='flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between'>
        <div className='grid flex-1 gap-4 sm:grid-cols-2 xl:grid-cols-4'>
          {REPORT_META_ITEMS.map((item) => (
            <ReportMetaItem key={item.id} value={report[item.id] || '-'} label={t(item.translationKey)} />
          ))}
        </div>
        <div className='flex flex-wrap gap-3'>
          <Button intent='secondary' size='md' onClick={onRegenerate} disabled={isGenerating}>
            <RefreshIcon className={cn('size-4', isGenerating && 'animate-spin')} />
            <span>{t('report.actions.regenerate')}</span>
          </Button>
        </div>
      </div>
    </Card>
  );
};
